import React, { useMemo, useState } from "react"
import { CreateNewSubscriptionScreenContent } from "./CreateNewSubscriptionScreenContent"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { CheckoutProvider } from "@stripe/react-stripe-js/checkout"
import { connect } from "react-redux"
import { apiRequest } from "../../data/redux/actions/api"
import { useCreateNewSubscriptionState } from "./hooks/useCreateNewSubscriptionState"
import { useGenerateAdminCheckoutSession } from "./hooks/useGenerateAdminCheckoutSession"
import { useNewSubscriberScreenRedirection } from "../hooks/useNewSubscriberScreenRedirection"
import { useSavedCardReservation } from "./hooks/useSavedCardReservation"
import { useHostedCheckoutFallback } from "./hooks/useHostedCheckoutFallback"

const SURFACE = "invoice_payment"

const InvoicePaymentScreenV2 = (props) => {
    const [checkoutSession, setCheckoutSession] = useState(null)

    const {
        stripePromise,
        plan,
        loadingUser,
        email,
        user,
        subscriptionMetadata,
        fastTrackData,
        emailB64,
    } = useCreateNewSubscriptionState(props, true)

    const { willRedirect } = useNewSubscriberScreenRedirection({
        fastTrackData: loadingUser ? null : fastTrackData,
        emailB64,
        redirectIfCreatedByAdmin: false,
    })

    // Le jeton de la vente, porté par le lien envoyé au parent.
    const sale = props.route?.params?.sale

    useGenerateAdminCheckoutSession({
        apiRequest: props.apiRequest,
        email,
        sale,
        setCheckoutSession,
        isLoading: loadingUser || willRedirect,
    })

    const reserveWithSavedCard = useSavedCardReservation({
        stripePromise,
        email,
        sale,
    })

    const hostedFallback = useHostedCheckoutFallback({
        email,
        surface: SURFACE,
    })

    // Detect if this is a stage/holiday course subscription
    const isStage = checkoutSession?.is_stage

    const loadingMessage = useMemo(() => {
        if (loadingUser) return "Chargement de vos informations..."
        if (props.isLoadingCheckout) return "Préparation de votre formule..."
        if (!checkoutSession?.client_secret) return "Préparation du paiement..."
        return null
    }, [loadingUser, props.isLoadingCheckout, checkoutSession?.client_secret])

    return (
        <ScreenSkeleton
            errorScreenProps={{ error: props.error || props.errorCheckout }}
            loadingMessage={loadingMessage}
            isLoading={
                loadingUser ||
                props.isLoadingCheckout ||
                !checkoutSession?.client_secret ||
                willRedirect
            }
        >
            <CheckoutProvider
                key={checkoutSession?.client_secret}
                stripe={stripePromise}
                options={{
                    fetchClientSecret: async () =>
                        checkoutSession.client_secret,
                }}
            >
                <CreateNewSubscriptionScreenContent
                    plan={plan}
                    schoolSubjects={props.schoolSubjects}
                    pricingTable={props.pricingTable}
                    sessionsPerWeek={props.sessionsPerWeek}
                    user={user}
                    checkoutSession={checkoutSession}
                    isStage={isStage}
                    holidayCourses={props.holidayCourses}
                    subscriptionMetadata={subscriptionMetadata}
                    savedCard={checkoutSession?.saved_card}
                    onReserveWithSavedCard={reserveWithSavedCard}
                    surface={SURFACE}
                    hostedFallback={hostedFallback}
                    // Même suite que le retour de Checkout (`return_url`) : le
                    // parent confirme les créneaux. `replace` démonte la page
                    // du lien, qui sinon redemanderait une session déjà réglée.
                    onReserved={() =>
                        props.navigation.replace("subscribe/possible-slots", {
                            user: emailB64,
                        })
                    }
                />
            </CheckoutProvider>
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        error: state.api.getSubscribePricing?.error,
        pricingTable: state.data.formData?.pricingTableV2,
        sessionsPerWeek: state.data.formData?.sessionsPerWeek,
        schoolSubjects: state.data.formData?.schoolSubjects ?? [],
        holidayCourses: state.data.formData?.holidayCourses,
        isLoadingCheckout: state.api.adminCreateSubscription?.request,
        errorCheckout: state.api.adminCreateSubscription?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InvoicePaymentScreenV2)
